/**
 * Platform Email Templates API Client
 * Handles all API calls to the KNKT Platform Emails backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5015/api/v1';

/**
 * Generic API request handler
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Platform Templates API
 */
export const templatesAPI = {
  /**
   * Get all platform templates
   * @param {object} params - Query parameters (category, search, page, limit)
   * @returns {Promise<{success: boolean, templates: Array}>}
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/platform-templates${queryString ? `?${queryString}` : ''}`;
    return apiRequest(endpoint);
  },

  /**
   * Get platform templates by category
   * @param {string} category - Category name
   * @returns {Promise<{success: boolean, templates: Array}>}
   */
  getByCategory: async (category) => {
    return apiRequest(`/platform-templates/category/${category}`);
  },

  /**
   * Get single template by MongoDB ID
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean, template: object}>}
   */
  getById: async (id) => {
    return apiRequest(`/platform-templates/id/${id}`);
  },

  /**
   * Get template by templateId (e.g., 'password-reset')
   * @param {string} templateId - Template ID
   * @returns {Promise<{success: boolean, template: object}>}
   */
  getByTemplateId: async (templateId) => {
    return apiRequest(`/platform-templates/${templateId}`);
  },

  /**
   * Create new platform template
   * @param {object} data - Template data
   * @returns {Promise<{success: boolean, template: object}>}
   */
  create: async (data) => {
    return apiRequest('/platform-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update platform template
   * @param {string} id - Template MongoDB ID
   * @param {object} data - Updated template data
   * @returns {Promise<{success: boolean, template: object}>}
   */
  update: async (id, data) => {
    return apiRequest(`/platform-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete platform template
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean}>}
   */
  delete: async (id) => {
    return apiRequest(`/platform-templates/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Duplicate platform template
   * @param {string} id - Template MongoDB ID
   * @param {string} newName - New template name
   * @returns {Promise<{success: boolean, template: object}>}
   */
  duplicate: async (id, newName) => {
    return apiRequest(`/platform-templates/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ newName }),
    });
  },

  /**
   * Publish template to AWS SES
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean, sesTemplateName: string, template: object}>}
   */
  publish: async (id) => {
    return apiRequest(`/platform-templates/${id}/publish`, {
      method: 'POST',
    });
  },

  /**
   * Unpublish template from AWS SES
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean}>}
   */
  unpublish: async (id) => {
    return apiRequest(`/platform-templates/${id}/unpublish`, {
      method: 'POST',
    });
  },

  /**
   * Preview template with sample data
   * @param {string} id - Template MongoDB ID
   * @param {object} data - Sample data for preview
   * @returns {Promise<{success: boolean, html: string, text: string, previewData: object}>}
   */
  preview: async (id, data = {}) => {
    return apiRequest(`/platform-templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  },

  /**
   * Render template with real data
   * @param {string} id - Template MongoDB ID
   * @param {object} data - Real template data
   * @returns {Promise<{success: boolean, html: string, text: string}>}
   */
  render: async (id, data) => {
    return apiRequest(`/platform-templates/${id}/render`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  },

  /**
   * Sync template to AWS SES (force re-sync)
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean}>}
   */
  sync: async (id) => {
    return apiRequest(`/platform-templates/${id}/sync`, {
      method: 'POST',
    });
  },

  /**
   * Send test email
   * @param {string} id - Template MongoDB ID
   * @param {string} testEmail - Recipient email address
   * @param {object} data - Sample data for test
   * @returns {Promise<{success: boolean, messageId: string}>}
   */
  test: async (id, testEmail, data = {}) => {
    return apiRequest(`/platform-templates/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ testEmail, data }),
    });
  },

  /**
   * Get template stats
   * @param {string} id - Template MongoDB ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getStats: async (id) => {
    return apiRequest(`/platform-templates/${id}/stats`);
  },
};

export default templatesAPI;
